// ============================================================================
// KLINISOL - News Kiosk Page (Enhanced)
// Features: Working filters, search, share, category colors, progress bar
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
    Newspaper, Mail, Calendar, Clock, User, ArrowRight, X, 
    Search, Filter, Link2, Check, Star, SortDesc
} from 'lucide-react';
import newsArticles from '../data/newsArticles';

const NewsKiosk = () => {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [activeCategory, setActiveCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('recent'); // 'recent' or 'featured'
    const [readProgress, setReadProgress] = useState(0);
    const [copied, setCopied] = useState(false);
    const [showAll, setShowAll] = useState(false); // NEW: Control View More
    const contentRef = useRef(null);

    // Category colors for left border accent
    const categoryColors = {
        'Regulatory Updates': '#E91E63',
        'Best Practices': '#475569',
        'Therapeutic Areas': '#2196F3',
        'Company News': '#4CAF50'
    };

    // Get unique categories with counts
    const categories = ['All', ...new Set(newsArticles.map(a => a.category))];
    const getCategoryCount = (cat) => {
        if (cat === 'All') return newsArticles.length;
        return newsArticles.filter(a => a.category === cat).length;
    };

    // Filter articles
    const filteredArticles = newsArticles.filter(article => {
        const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
        const matchesSearch = searchQuery === '' || 
            article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Sort articles
    const sortedArticles = [...filteredArticles].sort((a, b) => {
        if (viewMode === 'featured') {
            return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        }
        // Sort by date (most recent first) - assuming id correlates with recency
        return b.id - a.id;
    });

    // Separate featured and regular articles
    const featuredArticle = sortedArticles.find(a => a.featured);
    const regularArticles = sortedArticles.filter(a => !a.featured);
    
    // Show 6 articles initially, or all if showAll is true
    const INITIAL_DISPLAY_COUNT = 6;
    const articlesToShow = showAll ? regularArticles : regularArticles.slice(0, INITIAL_DISPLAY_COUNT);
    const hasMoreArticles = regularArticles.length > INITIAL_DISPLAY_COUNT;

    const handleNewsletterSignup = (e) => {
        e.preventDefault();
        if (email) {
            console.log('Newsletter signup:', email);
            setSubscribed(true);
            setEmail('');
            setTimeout(() => setSubscribed(false), 3000);
        }
    };

    const openArticle = (article) => {
        setSelectedArticle(article);
        setReadProgress(0);
        document.body.style.overflow = 'hidden';
    };

    const closeArticle = () => {
        setSelectedArticle(null);
        setReadProgress(0);
        document.body.style.overflow = 'unset';
    };

    // Track reading progress
    const handleScroll = (e) => {
        const element = e.target;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight - element.clientHeight;
        const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        setReadProgress(progress);
    };

    // Share functions
    const getArticleUrl = (article) => {
        return `${window.location.origin}/news-kiosk#article-${article.id}`;
    };

    const copyLink = () => {
        navigator.clipboard.writeText(getArticleUrl(selectedArticle));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shareLinkedIn = () => {
        const url = encodeURIComponent(getArticleUrl(selectedArticle));
        const title = encodeURIComponent(selectedArticle.title);
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    };

    const shareEmail = () => {
        const subject = encodeURIComponent(selectedArticle.title);
        const body = encodeURIComponent(
            `I thought you might find this article interesting:\n\n${selectedArticle.title}\n\n${selectedArticle.excerpt}\n\nRead more: ${getArticleUrl(selectedArticle)}`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-12 px-6">
                <div className="max-w-6xl mx-auto text-center">
                    <Newspaper className="w-14 h-14 mx-auto mb-3" />
                    <h1 className="text-4xl md:text-5xl font-bold mb-2">News Kiosk</h1>
                    <p className="text-lg opacity-90 max-w-3xl mx-auto">
                        Curated insights on clinical research regulations, trial best practices, and therapeutic area developments
                    </p>
                </div>
            </section>

            {/* Sticky Filter Bar */}
            <div className="sticky top-0 z-40 bg-white shadow-md border-b border-gray-200">
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        {/* Search */}
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent text-sm"
                            />
                        </div>

                        {/* Category Filters */}
                        <div className="flex flex-wrap items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500 hidden md:block" />
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                                        activeCategory === cat
                                            ? 'bg-[#E91E63] text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {cat} ({getCategoryCount(cat)})
                                </button>
                            ))}
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('recent')}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition-all ${
                                    viewMode === 'recent' 
                                        ? 'bg-white text-[#475569] shadow-sm' 
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <SortDesc className="w-3 h-3" />
                                Recent
                            </button>
                            <button
                                onClick={() => setViewMode('featured')}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition-all ${
                                    viewMode === 'featured' 
                                        ? 'bg-white text-[#475569] shadow-sm' 
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Star className="w-3 h-3" />
                                Featured
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <section className="py-8 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Results Count */}
                    <p className="text-sm text-gray-500 mb-6">
                        Showing {sortedArticles.length} article{sortedArticles.length !== 1 ? 's' : ''}
                        {activeCategory !== 'All' && ` in ${activeCategory}`}
                        {searchQuery && ` matching "${searchQuery}"`}
                    </p>

                    {/* No Results */}
                    {sortedArticles.length === 0 && (
                        <div className="text-center py-16">
                            <Newspaper className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-gray-500">No articles found matching your criteria.</p>
                            <button 
                                onClick={() => { setActiveCategory('All'); setSearchQuery(''); }}
                                className="mt-4 text-[#E91E63] font-semibold hover:underline"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}

                    {/* Featured Article */}
                    {featuredArticle && (
                        <div className="mb-12">
                            <div className="flex items-center gap-2 mb-4">
                                <Star className="w-5 h-5 text-[#E91E63]" />
                                <h2 className="text-2xl font-bold text-gray-900">Featured Article</h2>
                            </div>
                            <div 
                                onClick={() => openArticle(featuredArticle)}
                                className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer 
                                    transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1
                                    border-l-8"
                                style={{ borderLeftColor: categoryColors[featuredArticle.category] || '#475569' }}
                            >
                                <div className="p-8">
                                    {/* Category Badge */}
                                    <div className="flex items-center gap-2 mb-4">
                                        <span 
                                            className="px-3 py-1.5 rounded-full text-sm font-bold text-white"
                                            style={{ backgroundColor: categoryColors[featuredArticle.category] || '#475569' }}
                                        >
                                            {featuredArticle.category}
                                        </span>
                                        <span className="px-3 py-1.5 rounded-full text-sm font-bold bg-yellow-100 text-yellow-700 flex items-center gap-1">
                                            <Star className="w-4 h-4" />
                                            Featured
                                        </span>
                                    </div>
                                    
                                    {/* Title */}
                                    <h3 className="text-3xl font-bold text-gray-900 mb-4 hover:text-[#E91E63] transition-colors">
                                        {featuredArticle.title}
                                    </h3>
                                    
                                    {/* Excerpt */}
                                    <p className="text-gray-700 text-lg leading-relaxed mb-6">
                                        {featuredArticle.excerpt}
                                    </p>
                                    
                                    {/* Meta */}
                                    <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                                        <div className="flex items-center gap-4 text-sm text-gray-600">
                                            <span className="flex items-center gap-1.5">
                                                <User className="w-4 h-4" />
                                                {featuredArticle.author}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <Calendar className="w-4 h-4" />
                                                {featuredArticle.date}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <Clock className="w-4 h-4" />
                                                {featuredArticle.readTime}
                                            </span>
                                        </div>
                                        <button className="inline-flex items-center gap-2 px-6 py-3 bg-[#E91E63] text-white font-bold rounded-lg hover:bg-[#475569] transition-all shadow-md hover:shadow-lg">
                                            Read Article
                                            <ArrowRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Recent Articles Section */}
                    {regularArticles.length > 0 && (
                        <>
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Recent Articles</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    {showAll ? `Showing all ${regularArticles.length}` : `Showing ${Math.min(INITIAL_DISPLAY_COUNT, regularArticles.length)} of ${regularArticles.length}`} articles
                                </p>
                            </div>

                            {/* Articles Grid */}
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {articlesToShow.map(article => (
                                    <div 
                                        key={article.id} 
                                        onClick={() => openArticle(article)}
                                        className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer 
                                            transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1
                                            border-l-4"
                                        style={{ borderLeftColor: categoryColors[article.category] || '#475569' }}
                                    >
                                        <div className="p-5">
                                            {/* Category Badge */}
                                            <div className="flex items-center gap-2 mb-3">
                                                <span 
                                                    className="px-2 py-1 rounded text-xs font-semibold text-white"
                                                    style={{ backgroundColor: categoryColors[article.category] || '#475569' }}
                                                >
                                                    {article.category}
                                                </span>
                                            </div>
                                            
                                            {/* Title */}
                                            <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 hover:text-[#E91E63] transition-colors">
                                                {article.title}
                                            </h3>
                                            
                                            {/* Excerpt */}
                                            <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                                                {article.excerpt}
                                            </p>
                                            
                                            {/* Meta */}
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <div className="flex items-center gap-3">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {article.date}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {article.readTime}
                                                    </span>
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-[#E91E63]" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* View More / View Less Button */}
                            {hasMoreArticles && (
                                <div className="text-center mt-10">
                                    <button
                                        onClick={() => setShowAll(!showAll)}
                                        className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-[#475569] text-[#475569] font-bold rounded-lg hover:bg-[#475569] hover:text-white transition-all shadow-md hover:shadow-lg"
                                    >
                                        {showAll ? (
                                            <>
                                                View Less Articles
                                                <ArrowRight className="w-5 h-5 rotate-[-90deg]" />
                                            </>
                                        ) : (
                                            <>
                                                View All {regularArticles.length - INITIAL_DISPLAY_COUNT} More Articles
                                                <ArrowRight className="w-5 h-5 rotate-90" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* Newsletter Signup */}
                    <div className="mt-12 bg-white rounded-xl p-8 shadow-lg border-2 border-[#475569] hover:shadow-xl transition-all duration-300">
                        <div className="max-w-2xl mx-auto text-center">
                            <Mail className="w-10 h-10 mx-auto mb-4 text-[#E91E63]" />
                            <h3 className="text-2xl font-bold mb-2 text-[#E91E63]">Stay Updated</h3>
                            <p className="text-[#475569] mb-6">
                                Subscribe to receive the latest clinical research news and regulatory updates.
                            </p>
                            <form onSubmit={handleNewsletterSignup} className="flex gap-3 max-w-md mx-auto">
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="flex-1 px-4 py-3 rounded-lg text-gray-900 border border-gray-300 focus:ring-2 focus:ring-[#E91E63] focus:border-transparent"
                                    required
                                />
                                <button
                                    type="submit"
                                    className="px-6 py-3 bg-gradient-to-r from-[#E91E63] to-[#C2185B] hover:from-[#C2185B] hover:to-[#AD1457] rounded-lg font-semibold text-white transition-all shadow-md hover:shadow-lg"
                                >
                                    {subscribed ? 'Subscribed!' : 'Subscribe'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Article Modal */}
            {selectedArticle && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8 relative">
                        {/* Reading Progress Bar */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 rounded-t-xl overflow-hidden">
                            <div 
                                className="h-full bg-[#E91E63] transition-all duration-150"
                                style={{ width: `${readProgress}%` }}
                            />
                        </div>

                        {/* Close Button */}
                        <button 
                            onClick={closeArticle}
                            className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full border-2 border-gray-300 hover:border-[#E91E63] hover:bg-[#E91E63] hover:text-white transition-all duration-300 flex items-center justify-center shadow-md z-10"
                            aria-label="Close article"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {/* Article Header */}
                        <div 
                            className="text-white p-8 rounded-t-xl"
                            style={{ 
                                background: `linear-gradient(to right, ${categoryColors[selectedArticle.category] || '#475569'}, #475569)` 
                            }}
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold border border-white/30">
                                    {selectedArticle.category}
                                </span>
                                <span className="text-sm opacity-90 flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {selectedArticle.date}
                                </span>
                                <span className="text-sm opacity-90 flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {selectedArticle.readTime}
                                </span>
                            </div>
                            
                            <h2 className="text-3xl font-bold mb-3">
                                {selectedArticle.title}
                            </h2>
                            
                            <div className="flex items-center gap-2 text-sm opacity-90">
                                <User className="w-4 h-4" />
                                <span>By {selectedArticle.author}</span>
                            </div>
                        </div>

                        {/* Article Content */}
                        <div 
                            ref={contentRef}
                            onScroll={handleScroll}
                            className="p-8 max-h-[50vh] overflow-y-auto"
                        >
                            <div 
                                className="prose prose-lg max-w-none
                                    prose-headings:text-[#475569] 
                                    prose-p:text-gray-700 prose-p:leading-relaxed
                                    prose-a:text-[#E91E63] prose-a:font-semibold prose-a:no-underline hover:prose-a:text-[#475569] prose-a:transition-colors
                                    prose-strong:text-[#475569]
                                    prose-ul:text-gray-700
                                    prose-li:my-1"
                                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                            />
                        </div>

                        {/* Article Footer with Share */}
                        <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-xl">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                {/* Share Buttons */}
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 mr-2">Share:</span>
                                    <button 
                                        onClick={copyLink}
                                        className="p-2 rounded-lg bg-white border border-gray-300 hover:border-[#E91E63] hover:text-[#E91E63] transition-colors flex items-center gap-1"
                                        title="Copy link"
                                    >
                                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
                                        <span className="text-xs">{copied ? 'Copied!' : 'Link'}</span>
                                    </button>
                                    <button 
                                        onClick={shareLinkedIn}
                                        className="p-2 rounded-lg bg-white border border-gray-300 hover:border-[#0077B5] hover:text-[#0077B5] transition-colors flex items-center gap-1"
                                        title="Share on LinkedIn"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                        </svg>
                                        <span className="text-xs">LinkedIn</span>
                                    </button>
                                    <button 
                                        onClick={shareEmail}
                                        className="p-2 rounded-lg bg-white border border-gray-300 hover:border-[#E91E63] hover:text-[#E91E63] transition-colors flex items-center gap-1"
                                        title="Share via email"
                                    >
                                        <Mail className="w-4 h-4" />
                                        <span className="text-xs">Email</span>
                                    </button>
                                </div>
                                
                                <button 
                                    onClick={closeArticle}
                                    className="px-6 py-2 bg-white text-[#475569] font-semibold rounded-lg border-2 border-[#475569] hover:bg-[#475569] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">KLINiSOL</h3>
                    <p className="text-sm opacity-90 mb-2">
                        &copy; 2025 KLINISOL. All rights reserved.
                    </p>
                    <Link 
                        to="/terms-and-conditions" 
                        className="text-xs opacity-75 hover:opacity-100 hover:underline transition-opacity mb-4 inline-block"
                    >
                        Terms & Conditions
                    </Link>
                    
                    {/* Social Media Links */}
                    <div className="flex items-center justify-center gap-4 mt-4">
                        <a 
                            href="https://www.linkedin.com/company/klinisol/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110 duration-300"
                            aria-label="LinkedIn"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                        </a>
                        
                        <a 
                            href="https://youtube.com/@klinisol721" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110 duration-300"
                            aria-label="YouTube"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default NewsKiosk;
